import { useMemo, useState } from 'react';
import PageHeader from '@/components/feature/PageHeader';
import ContentCard from '@/components/feature/ContentCard';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import { useSEO } from '@/hooks/useSEO';
import { newsItems } from '@/hooks/useContent';

const TAGS = ['全部', '影视', '活动', '八卦'];
const PAGE_SIZE = 6;

export default function NewsCategoryPage() {
  const [activeTag, setActiveTag] = useState('全部');
  const [sort, setSort] = useState<'latest' | 'hot'>('latest');
  const [page, setPage] = useState(1);

  useSEO(
    '娱乐资讯 - 一起艹',
    '最新、最热门的娱乐资讯汇聚于此，覆盖影视动态、明星八卦与线下活动，助你第一时间掌握娱乐风向。',
    '娱乐资讯,影视动态,明星八卦,活动,一起艹',
  );

  const filtered = useMemo(() => {
    const byTag = activeTag === '全部' ? newsItems : newsItems.filter((item) => item.tags.includes(activeTag));
    return [...byTag].sort((a, b) =>
      sort === 'latest' ? b.publishedAt.localeCompare(a.publishedAt) : b.views - a.views,
    );
  }, [activeTag, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const handleTag = (tag: string) => {
    setActiveTag(tag);
    setPage(1);
  };

  const reset = () => {
    setActiveTag('全部');
    setSort('latest');
    setPage(1);
  };

  return (
    <div className="w-full">
      <PageHeader
        title="娱乐资讯"
        subtitle="最新、热门娱乐资讯汇聚，按标签筛选与热度排序，快速找到你关心的内容。"
        crumbs={[{ label: '首页', to: '/' }, { label: '娱乐资讯' }]}
        icon="ri-newspaper-line"
      />

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 md:mb-8">
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 w-fit overflow-x-auto">
            {TAGS.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => handleTag(tag)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  activeTag === tag
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-primary-600'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-foreground-400">排序</span>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100">
              {[
                { key: 'latest', label: '最新' },
                { key: 'hot', label: '最热' },
              ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => {
                    setSort(option.key as 'latest' | 'hot');
                    setPage(1);
                  }}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    sort === option.key
                      ? 'bg-secondary-700 text-background-50'
                      : 'text-foreground-600 hover:text-secondary-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {pageItems.length === 0 ? (
          <EmptyState
            icon="ri-newspaper-line"
            title="暂无符合条件的资讯"
            description="当前标签下还没有内容，试试切换其他标签或重置筛选条件。"
            actionLabel="重置筛选"
            onAction={reset}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {pageItems.map((item) => (
                <ContentCard key={item.id} item={item} />
              ))}
            </div>
            <Pagination page={current} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
}